import { useCallback, useEffect, useState } from 'react';
import type { DiscordConnection } from '@playerbase/types';

import { useShell } from '../../shell/use-shell.js';
import { fetchDiscordConnectionForGame } from '../../games/discord-data.js';
import { useDiscordConnectionRealtime } from '../../games/use-discord-connection-realtime.js';
import { ConnectionCard } from './discord/connection-card.js';

// ---------------------------------------------------------------------------
// Discord connection sub-page.
//
// Just the server install / reconnect / disconnect surface. Channel
// selection used to live here but moved to Feedback Settings > Watched
// channels - that's where studios decide what messages the bot ingests,
// while this page stays focused on the underlying server connection.
// ---------------------------------------------------------------------------

export function GameSettingsDiscordPage(): JSX.Element {
  const { activeStudio, activeGame } = useShell();
  const [connection, setConnection] = useState<DiscordConnection | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    if (activeGame === null) {
      setConnection(null);
      setLoading(false);
      return;
    }
    // First load shows the loading placeholder; later refetches (e.g.
    // Realtime pushes after the bot rewrites last_health_check_at) refetch
    // silently so the card doesn't flash on every recheck.
    const next = await fetchDiscordConnectionForGame(activeGame.id);
    setConnection(next);
    setLoading(false);
  }, [activeGame]);

  useEffect(() => {
    void load();
  }, [load]);

  useDiscordConnectionRealtime(connection?.id ?? null, () => {
    void load();
  });

  if (activeGame === null) {
    return (
      <div className="px-4 py-10 sm:px-6">
        <p className="text-sm text-text-muted">
          Select a game to manage its Discord connection.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <header>
        <h1 className="font-display text-2xl font-bold text-text">Discord connection</h1>
        <p className="mt-2 text-sm text-text-muted">
          Manage the Discord server PlayerBase connects to for {activeGame.name}.
        </p>
      </header>

      {loading ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : (
        <ConnectionCard
          studioId={activeStudio.id}
          gameId={activeGame.id}
          connection={connection}
          onChanged={load}
        />
      )}
      </div>
    </div>
  );
}
